import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { NotificationResponse } from '../interfaces/notification-response.interface';
import { IconClasses } from '../models/icon-classes.enum';
import { NotificationType } from '../models/notification-type.enum';
import { Notification } from '../models/notification.model';

@Injectable()
export class NotificationService {
  /**
   * Additional timeout if there's an error.
   *
   * @private
   * @memberof NotificationService
   */
  private readonly extendedTimeout = 15000;

  /**
   * Configuration for the error messages.
   *
   * @private
   * @type {Partial<IndividualConfig>}
   * @memberof NotificationService
   */
  private readonly errorConfig: Partial<IndividualConfig> = {
    timeOut: this.extendedTimeout,
  };
  constructor(private readonly toastr: ToastrService, private readonly translate: TranslateService) {}

  /**
   * Handles all response. If there's no type, Info is the default.
   *
   * @param {NotificationResponse[]} notifications
   * @returns {void}
   * @memberof NotificationService
   */
  public handleResponse(notifications: NotificationResponse[]): void {
    if (!notifications) {
      return;
    }

    notifications
      .map((notificationResponse) => new Notification(notificationResponse))
      .forEach((notification) => this.notify(notification));
  }

  /**
   * Handles only the successfull notifications
   *
   * @param {NotificationResponse[]} notifications
   * @returns {void}
   * @memberof NotificationService
   */
  public handleSuccess(notifications: NotificationResponse[]): void {
    if (!notifications) {
      return;
    }

    notifications
      .filter((i) => i.status === NotificationType.success)
      .forEach((element) => {
        this.success(element.message, element.title);
      });
  }

  /**
   * Basic notify. Error/Info/Success.
   *
   * @param {Notification} notification
   * @memberof NotificationService
   */
  public notify(notification: Notification) {
    if (notification.getToastType() === IconClasses.error) {
      this.error(notification.message, notification.title);
    } else {
      this.show(notification);
    }
  }

  /**
   * Notify error
   *
   * @param {string} title
   * @param {string} message
   * @memberof NotificationService
   */
  public error(title: string, message: string) {
    this.toastr.error(
      this.translate.instant(message, { attribute: title }),
      new TitleCasePipe().transform(title),
      this.errorConfig
    );
  }

  /**
   * Clears all natifications
   *
   * @memberof NotificationService
   */
  public clearAll() {
    this.toastr.clear();
  }

  /**
   * Notify with success
   *
   * @param {string} title
   * @param {string} message
   * @memberof NotificationService
   */
  public success(title: string, message: string) {
    this.toastr.success(this.translate.instant(message), new TitleCasePipe().transform(this.translate.instant(title)));
  }

  private show(notification: Notification) {
    this.toastr.show(
      this.translate.instant(notification.message),
      new TitleCasePipe().transform(this.translate.instant(notification.title)),
      null,
      notification.getToastType()
    );
  }
}
